import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Input from '../../Components/Input/Input';
import Button from '../../Components/Button/Button';
import { handleValidationErrors, validateForm, validatePassword } from '../../Utils/validationHelper';
import { postApi } from '../../Services/apiService';
import { toast } from 'react-toastify';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { clearSession } from '../../Utils/postLoginNavigation';

const Signup = () => {
  const [searchParams] = useSearchParams();
  const role = searchParams.get('role');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    role: role || '',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  // Create refs for all input fields (must be called before any conditional returns)
  const inputRefs = {
    firstName: useRef(null),
    lastName: useRef(null),
    email: useRef(null),
    phoneNumber: useRef(null),
    password: useRef(null),
  };

  // Redirect to role selection if no role is provided
  useEffect(() => {
    if (!role || (role !== 'landlord' && role !== 'tenant')) {
      navigate('/choose-role');
    }
  }, [role, navigate]);

  const redirectTo = searchParams.get('redirect');
  const asRole = searchParams.get('as');

  useEffect(() => {
    // Update role when URL changes
    const urlRole = searchParams.get('role');
    if (urlRole && (urlRole === 'landlord' || urlRole === 'tenant')) {
      setFormData((prev) => ({ ...prev, role: urlRole }));
    }
  }, [searchParams]);

  useEffect(() => {
    // Add animation classes on mount
    const infoSection = document.querySelector('.signup-info');
    const formSection = document.querySelector('.signup-form-section');
    const formCard = document.querySelector('.signup-form-card');
    
    if (infoSection) infoSection.classList.add('animate-fade-in-left');
    if (formSection) formSection.classList.add('animate-fade-in-right');
    if (formCard) formCard.classList.add('animate-scale-in');
  }, []);

  // Don't render form if no valid role (after all hooks are called)
  if (!role || (role !== 'landlord' && role !== 'tenant')) {
    return null;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Define validation rules
    const validationRules = {
      firstName: {
        required: true,
        message: {
          required: 'First name is required',
        },
      },
      lastName: {
        required: true,
        message: {
          required: 'Last name is required',
        },
      },
      email: {
        required: true,
        pattern: /\S+@\S+\.\S+/,
        message: {
          required: 'Email is required',
          pattern: 'Email is invalid',
        },
      },
      phoneNumber: {
        required: true,
        message: {
          required: 'Phone number is required',
        },
      },
      password: {
        required: true,
        custom: (value) => {
          return validatePassword(value);
        },
        message: {
          required: 'Password is required',
        },
      },
    };

    // Validate the form
    const newErrors = validateForm(formData, validationRules);

    // Handle errors (will focus on first error input)
    if (handleValidationErrors(newErrors, inputRefs, setErrors)) {
      return;
    }

    // Extract country code and phone number separately from the full value
    const cleanedPhone = (formData.phoneNumber || '').replace(/\s/g, '');
    const digitsOnly = cleanedPhone.replace(/\D/g, '');
    const countryCodeMatch = digitsOnly.match(/^\d{1,4}/);
    const numericCountryCode = countryCodeMatch ? countryCodeMatch[0] : '';
    const phoneNumberDigits = numericCountryCode
      ? digitsOnly.slice(numericCountryCode.length)
      : digitsOnly;

    // Map role string to API role id
    const roleId =
      formData.role === 'landlord' ? '1' :
      formData.role === 'tenant' ? '2' :
      '';

    const payload = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      password: formData.password,
      role: roleId,
      phoneNumber: phoneNumberDigits,
      countryCode: numericCountryCode ? `+${numericCountryCode}` : '',
    };

    setIsLoading(true);
    setErrors({});

    try {
      // Call signup API
      const response = await postApi('/auth/signup', payload);

      // Treat both 200 and 201 as success (201 = created)
      if (response?.statusCode === 200 || response?.statusCode === 201) {
        toast.success(response?.message || 'Account created successfully. Please verify your email.');

        // Nothing from an earlier session may survive into a fresh signup -
        // a leftover userData plus this pre-OTP token would otherwise be enough
        // to walk straight into a dashboard.
        clearSession();

        // Store token from signup response (needed for authenticated next steps)
        const signupData = response?.data || response;
        const signupToken = signupData?.token || signupData?.user_details?.token;
        if (signupToken) {
          localStorage.setItem('token', signupToken);
        }

        // Store email and role for OTP verification
        localStorage.setItem('signupEmail', formData.email);
        localStorage.setItem('signupRole', formData.role);

        // Pass redirect so after OTP we can send user to the property page (e.g. from "Start Tenant Screening")
        const redirect = redirectTo || undefined;
        navigate('/verify-otp', { state: { email: formData.email, role: formData.role, redirect } });
      } else {
        const errorMessage = response?.message || 'Signup failed. Please try again.';
        setErrors({ submit: errorMessage });
        toast.error(errorMessage);
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'Signup failed. Please try again.';
      setErrors({ submit: errorMessage });
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="signup-page">
      <div className="signup-container">
        <div className="signup-info">
          <div className="signup-logo">
            <Link to="/" onClick={() => localStorage.clear()}>
              <img src={window.getPublicUrl("/assets/images/Logo.png")} alt="ClearRent" className="signup-logo-img" />
            </Link>
          </div>
          <h1 className="signup-title">
            {formData.role === 'tenant' 
              ? 'Apply for properties with ease' 
              : 'Simplifying tenant Screening for landlords'}
          </h1>
          <p className="signup-subtitle">
            {formData.role === 'tenant'
              ? 'Create your account and apply for properties quickly and securely.'
              : 'Create your account and get instant access to automated tenant screening.'}
          </p>
          <ul className="signup-features">
            {formData.role === 'tenant' ? (
              <>
                <li>
                  <img src={window.getPublicUrl("/assets/images/CircleCheckWIthBackground.svg")} alt="Check" className="signup-feature-icon" />
                  <span>Quick application process</span>
                </li>
                <li>
                  <img src={window.getPublicUrl("/assets/images/CircleCheckWIthBackground.svg")} alt="Check" className="signup-feature-icon" />
                  <span>Secure document upload & verification</span>
                </li>
                <li>
                  <img src={window.getPublicUrl("/assets/images/CircleCheckWIthBackground.svg")} alt="Check" className="signup-feature-icon" />
                  <span>Automated reference checks</span>
                </li>
              </>
            ) : (
              <>
                <li>
                  <img src={window.getPublicUrl("/assets/images/CircleCheckWIthBackground.svg")} alt="Check" className="signup-feature-icon" />
                  <span>Unlimited property listings</span>
                </li>
                <li>
                  <img src={window.getPublicUrl("/assets/images/CircleCheckWIthBackground.svg")} alt="Check" className="signup-feature-icon" />
                  <span>Automated voice interviews & document verification</span>
                </li>
                <li>
                  <img src={window.getPublicUrl("/assets/images/CircleCheckWIthBackground.svg")} alt="Check" className="signup-feature-icon" />
                  <span>Automated reference checks</span>
                </li>
              </>
            )}
          </ul>
        </div>
        <div className="signup-form-section">
          <h2 className="signup-form-title">Create Your Profile</h2>
          <p className="signup-form-subtitle">
            {formData.role === 'tenant'
              ? 'Create your profile to apply for properties and complete screening quickly.'
              : 'Create your profile to manage properties and screening tenants with automated technology.'}
          </p>
          <form onSubmit={handleSubmit} className={`signup-form-card ${Object.keys(errors).length > 0 ? 'has-errors' : ''}`} noValidate>
            <div className="signup-form-header">Account Information</div>
            <div className={`signup-form-fields ${Object.keys(errors).length > 0 ? 'has-errors' : ''}`}>
              <div className="signup-name-row">
                <Input
                  ref={inputRefs.firstName}
                  type="text"
                  name="firstName"
                  label="First Name"
                  placeholder="Eg. John"
                  value={formData.firstName}
                  onChange={handleChange}
                  error={errors.firstName}
                  required
                />
                <Input
                  ref={inputRefs.lastName}
                  type="text"
                  name="lastName"
                  label="Last Name"
                  placeholder="Eg. Dey"
                  value={formData.lastName}
                  onChange={handleChange}
                  error={errors.lastName}
                  required
                />
              </div>
              <Input
                ref={inputRefs.email}
                type="email"
                name="email"
                label="Email"
                placeholder="Eg. JohnDey4533@gmail.com"
                value={formData.email}
                onChange={handleChange}
                error={errors.email}
                required
              />
              <div className="signup-phone-field">
                <label className="signup-phone-label">
                  Phone
                  <span className="required-asterisk">*</span>
                </label>
                <PhoneInput
                  country={'us'}
                  value={formData.phoneNumber}
                  onChange={(value) =>
                    setFormData((prev) => ({ ...prev, phoneNumber: value }))
                  }
                  countryCodeEditable={false}
                  inputClass={`signup-phone-input ${errors.phoneNumber ? 'input-error' : ''}`}
                  containerClass="signup-phone-input-container"
                  buttonClass="signup-phone-country-button"
                  dropdownClass="signup-phone-country-dropdown"
                  enableSearch
                  searchPlaceholder="Search country"
                  placeholder="Enter phone number"
                  inputProps={{
                    name: 'phoneNumber',
                    ref: inputRefs.phoneNumber,
                  }}
                />
                {errors.phoneNumber && (
                  <div className="error-message" style={{ marginTop: '0.25rem' }}>
                    {errors.phoneNumber}
                  </div>
                )}
              </div>
              <div className="signup-password-wrapper">
                <label htmlFor="password" className="input-label">
                  Password
                  <span className="required-asterisk">*</span>
                </label>
                <div className="signup-password-input-wrapper">
                  <input
                    ref={inputRefs.password}
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    placeholder="Eg. 45234kdk45"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className={`input-field ${errors.password ? 'input-error' : ''}`.trim()}
                  />
                  <button
                    type="button"
                    className="signup-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <img 
                      src={window.getPublicUrl ? window.getPublicUrl(showPassword ? "/assets/images/Eye.svg" : "/assets/images/eye-closed.svg") : (showPassword ? "/assets/images/Eye.svg" : "/assets/images/eye-closed.svg")} 
                      alt={showPassword ? "Hide password" : "Show password"} 
                      className="signup-eye-icon"
                    />
                  </button>
                </div>
                {errors.password && (
                  <div className="error-message" style={{ marginTop: '0.25rem' }}>
                    {errors.password}
                  </div>
                )}
              </div>
            </div>
            <Button 
              type="submit" 
              variant="success" 
              className="signup-submit-button"
              disabled={isLoading}
            >
              {isLoading ? 'Creating Account...' : 'Continue'}
            </Button>
            <p className="signup-signin-link">
              Already have an account?{' '}
              <Link to={redirectTo && asRole ? `/login?redirect=${encodeURIComponent(redirectTo)}&as=${asRole}` : '/login'}>
                Sign in
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Signup;
